import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000InputDecimal from "../../../u2000Core/components/formControls/input/U2000InputDecimal";
import { U2000FormStateEnumState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000CheckBox from "../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000CheckBoxBase from "../../../u2000Core/components/formControls/checkbox/U2000CheckBoxBase";
import U2000YesNoDialog from "../../../u2000Core/components/dialog/U2000YesNoDialog";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU3129TimesheetWeekView } from "./U3129TimesheetWeekViewContext";
import {
  U2000_TIMESHEET_WEEK_DAYS,
  u2000SumHonorariumDayTotals,
  type U2000TimesheetWeekDay,
} from "../../../u2000Core/tools/U2000TimesheetHonorariumDayTotals";
import {
  u2000SumCrossTabAbsenceHoursFooter,
  u2000SumCrossTabSpecialNetHoursFooter,
} from "../../../u2000Core/tools/U2000TimesheetBankAggregates";
import { useU3129Context } from "../../U3129Context";
import {
  u3129BuildFindTasksForProjectCacheKey,
  u3129InvalidateFindTasksForProjectCache,
  useU3129FindProjectHandlers,
  useU3129FindTasksForProjectHandlers,
} from "../../U3129UseFindProjectSubprojectHandlers";
import {
  ensureBankHoursList,
  newHonorariumClientRowId,
  type U3129FindProjectItem,
  type U3129FindSubprojectItem,
  type U3129HonorariumItem,
  type U3129TaskItem,
  type U3129Timesheet,
} from "../../U3129Service";
import { NS, U3129ROLES } from "../../U3129";
import { U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS } from "../../tools/u3129ReadonlyTableCellClass";
import {
  U3129_GRID_COMPACT_FOOTER_GUTTER_TD_CLASS,
  U3129_GRID_COMPACT_FOOTER_ROW_PY,
  U3129_GRID_COMPACT_FOOTER_LABEL_TD_BASE,
  U3129_GRID_COMPACT_HOUR_CELL_INNER_CLASS,
  U3129_GRID_COMPACT_HOUR_FOOTER_VALUE_TD_CLASS_EDIT,
  U3129_GRID_COMPACT_HOUR_FOOTER_VALUE_TD_CLASS_READ,
  U3129_GRID_COMPACT_ROW_CELL_PY,
  U3129_GRID_COMPACT_ROW_CHECKBOX_TD_CLASS,
  U3129_GRID_COMPACT_ROW_HOUR_TD_BASE,
  U3129_GRID_COMPACT_ROW_TASK_TD_BASE,
  U3129_GRID_COMPACT_DECIMAL_CONTROL_CLASS,
  U3129_GRID_COMPACT_TABLE_CONTROL_CLASS,
  U3129_HONORARIUM_CUSTOMER_COL_CLASS,
  U3129_HONORARIUM_FOOTER_LABEL_COL_CLASS,
  U3129_HONORARIUM_HOUR_COL_CLASS,
  U3129_HONORARIUM_PROJECT_COL_CLASS,
  U3129_HONORARIUM_SUBPROJECT_COL_CLASS,
  U3129_HONORARIUM_TASK_COL_CLASS,
  U3129_HONORARIUM_TASK_DETAIL_COLSPAN_CLASS,
  U3129_HONORARIUM_TASK_DETAIL_TD_BASE,
  U3129_HONORARIUM_TASK_ORPHAN_HEADER_CLASS,
  U3129_HONORARIUM_COL_WIDTH_CLIENT,
  U3129_HONORARIUM_COL_WIDTH_HOUR,
  U3129_HONORARIUM_COL_WIDTH_NATURES,
  U3129_HONORARIUM_COL_WIDTH_PROJECT,
  U3129_HONORARIUM_COL_WIDTH_SUBPROJECT,
  U3129_HONORARIUM_COL_WIDTH_TASK_ORPHAN,
  U3129_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS,
  U3129_HONORARIUM_EXPANDED_CHILD_BOX_CLASS,
  U3129_HONORARIUM_COL_WIDTH_GUTTER,
  U3129_HONORARIUM_EXPANDED_CHILD_ROW_CLASS,
  U3129_HONORARIUM_GUTTER_TD_CLASS,
  U3129_HONORARIUM_META_COLUMN_CELL_X,
  U3129_HONORARIUM_NATURES_COL_CLASS,
  U3129_HONORARIUM_TASK_ROW_META_COLSPAN,
  U3129_HONORARIUM_TASK_ROW_NATURE_PART_CLASS,
  U3129_HONORARIUM_TASK_ROW_SPLIT_WRAPPER_CLASS,
  U3129_HONORARIUM_TASK_ROW_TASK_PART_CLASS,
  u3129HonorariumTableMinWidth,
} from "../../tools/u3129TimesheetGridCompactStyles";
import {
  u3129ApplyTaskSortToProjectGroup,
  u3129SortHonorariumProjectGroups,
  type U3129HonorariumProjectSortField,
  type U3129HonorariumSortOrder,
} from "../../tools/u3129HonorariumGridSort";
import {
  u3129HonorariumTaskWorkNatureFieldsFromTaskItem,
  u3129TaskWorkNatureDescriptionForLanguage,
} from "../../tools/u3129HonorariumWorkNatureDisplay";
import U3129HonorariumSortableMetaHeader from "./U3129HonorariumSortableMetaHeader";
import {
  getHonorariumTemplateStorageKey,
  parseHonorariumTemplatePayload,
  serializeHonorariumTemplate,
  templateRowsToHonorariumItems,
  U3129_HONORARIUM_TEMPLATE_CHANGED_EVENT,
  u3129FindTaskInHonorariumCache,
  u3129HonorariumTemplateScopeKey,
  u3129PartitionHonorariumTemplateRowsByActiveAssignments,
  u3129UniqueFindTasksBodiesFromTemplateRows,
  writeHonorariumTemplateToStorage,
  type U3129HonorariumTemplateScopeResolution,
} from "../../tools/u3129HonorariumTemplate";
import { U3129LineProjectSubprojectRowCells } from "./U3129LineProjectSubprojectRowCells";
import U3129AddHonorariumLinesDialog from "./U3129AddHonorariumLinesDialog";
import { formatU3129TaskLabel } from "../../tools/u3129HonorariumTaskLabel";
import { u3129FilterHonorariumLinesNotDuplicatingExisting } from "../../tools/u3129BuildHonorariumLinesFromTasks";
import {
  U3129_HONORARIUM_NO_PROJECT_KEY,
  u3129GroupHonorariumForDisplay,
  u3129ResolveHonorariumProjectKey,
} from "../../tools/u3129GroupHonorariumForDisplay";
import {
  u3129HonorariumHourFieldId,
  u3129ParseHonorariumHourFieldId,
  u3129ResolveHonorariumHourCellAfterArrow,
  type U3129HonorariumHourCellCoord,
} from "../../tools/u3129ResolveHonorariumHourCellAfterArrow";
import U3129HonorariumProjectHeaderRow from "./U3129HonorariumProjectHeaderRow";
import U3129HonorariumSubprojectHeaderRow from "./U3129HonorariumSubprojectHeaderRow";
import {
  u3129HonorariumDeleteConfirmPreview,
  u3129HonorariumGroupCheckState,
  u3129HonorariumHeaderCheckState,
  u3129HonorariumIndicesForSelectAllToggle,
  type U3129HonorariumDeleteConfirmKind,
  type U3129HonorariumDeleteConfirmPreview,
} from "../../tools/u3129HonorariumProjectSelection";
import type { U3129HonorariumCoherenceWarningsState } from "../../tools/u3129HonorariumCoherenceWarnings";
import {
  u3129HonorariumCoherenceLineKey,
  u3129HonorariumCoherenceMessageForProjectGroup,
} from "../../tools/u3129HonorariumCoherenceWarnings";
import U3129HonorariumCoherenceIcon from "./U3129HonorariumCoherenceIcon";
import { FaBookmark, FaClone, FaPlus, FaRegClock, FaTrash } from "react-icons/fa";
import U3147DayDetailReadonlyDialog from "../../../u3147TimeTracking/components/common/U3147DayDetailReadonlyDialog";
import { useU3147WeekDaySessions } from "../../../u3147TimeTracking/shared/useU3147WeekDaySessions";
import { U3147ROLES } from "../../../u3147TimeTracking/U3147";
import { formatCurrency, type CurrencyFormatOptions } from "../../../u2000Core/tools/U2000CurrencyUtils";

const DAYS = U2000_TIMESHEET_WEEK_DAYS;

const U3129_HONORARIUM_HOUR_GRID_ARROW_KEYS = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]);

function u3129FocusHonorariumHourCell(coord: U3129HonorariumHourCellCoord): void {
  const el = document.getElementById(u3129HonorariumHourFieldId(coord.index, coord.day));
  if (el instanceof HTMLElement) {
    el.focus();
    el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
}

/** Même présentation que U2000InputDecimal (défauts) pour la colonne Total = somme des jours (sans état interne async). */
const HORIZONTAL_ROW_TOTAL_FORMAT: CurrencyFormatOptions = {
  currencySymbol: "",
  symbolPosition: "left",
  decimalSeparator: ".",
  thousandSeparator: " ",
  allowNegative: true,
  showNegativeInParentheses: false,
  decimalDigits: 2,
};

function formatHorizontalRowTotalHours(total: number): string {
  return formatCurrency(total, HORIZONTAL_ROW_TOTAL_FORMAT);
}

/** Heures d’un jour sur une ligne (lecture seule) — aligné sur `rowTotalHours` / `Number(x)||0`. */
function formatHonorariumDayCellHours(value: unknown): string {
  return formatCurrency(Number(value) || 0, HORIZONTAL_ROW_TOTAL_FORMAT);
}

const U3129_HOUR_COL_CLASS = U3129_HONORARIUM_HOUR_COL_CLASS;
const U3129_PROJECT_COL_CLASS = U3129_HONORARIUM_PROJECT_COL_CLASS;
const U3129_CUSTOMER_COL_CLASS = U3129_HONORARIUM_CUSTOMER_COL_CLASS;
const U3129_SUBPROJECT_COL_CLASS = U3129_HONORARIUM_SUBPROJECT_COL_CLASS;
const U3129_TASK_COL_CLASS = U3129_HONORARIUM_TASK_COL_CLASS;
const U3129_TASK_ORPHAN_SLOT_TD_CLASS = `${U3129_HONORARIUM_TASK_ORPHAN_HEADER_CLASS} p-0 border-0 align-top`;
const U3129_TOTAL_LABEL_COL_CLASS = U3129_HONORARIUM_FOOTER_LABEL_COL_CLASS;
const U3129_HONORARIUM_TEXT_TRUNCATE_CLASS = "min-w-0 max-w-full truncate";
const U3129_HONORARIUM_META_COL_CLASSES = {
  project: U3129_PROJECT_COL_CLASS,
  customer: U3129_CUSTOMER_COL_CLASS,
  natures: U3129_HONORARIUM_NATURES_COL_CLASS,
  subproject: U3129_SUBPROJECT_COL_CLASS,
};
const U3129_PROJECT_DAY_HOUR_TH_CLASS = `pl-1 pr-[calc(0.25rem+1rem+1px)] py-2.5 text-right tabular-nums ${U3129_HOUR_COL_CLASS}`;
function rowTotalHours(row: U3129HonorariumItem | undefined): number {
  if (!row) return 0;
  return DAYS.reduce((acc, d) => acc + (Number(row[d]) || 0), 0);
}

interface U3129HonorariumTabProps {
  formStateHandlers: U2000FormStateHandlers<U3129Timesheet>;
  coherenceWarnings?: U3129HonorariumCoherenceWarningsState | null;
}

interface HonorariumRowEditorProps {
  index: number;
  formStateHandlers: U2000FormStateHandlers<U3129Timesheet>;
  findProjectHandlers: ReturnType<typeof useU3129FindProjectHandlers>;
  getProjectId: () => string | undefined | null;
  /** Clé stable pour le cache des tâches (projet/sous-projet) et le payload de sauvegarde. */
  rowClientId: string;
  readonly: boolean;
  /** Désactiver la case à cocher (ex. une seule ligne visible). */
  checkboxDisabled?: boolean;
  /** Personnalisation initLU : affichage colonne sous-projet. */
  showSubProjectColumn: boolean;
  /** Afficher la colonne Sous-projet pour cette ligne (projet sélectionné includeSubProjet === true). */
  rowIncludeSubProject: boolean;
  visibleDays: readonly U2000TimesheetWeekDay[];
  /** Ligne tâche sous un en-tête projet/sous-projet (sans répéter projet/client/sous-projet). */
  taskOnlyLayout?: boolean;
  taskCoherenceMessage?: string;
}

function HonorariumRowEditor({
  index,
  formStateHandlers,
  findProjectHandlers,
  getProjectId,
  rowClientId,
  readonly,
  checkboxDisabled,
  showSubProjectColumn,
  rowIncludeSubProject,
  visibleDays,
  taskOnlyLayout = false,
  taskCoherenceMessage = "",
}: HonorariumRowEditorProps) {
  const { t, i18n } = useTranslation();
  const { setHonorariumTasksForRow } = useU3129Context();
  const { fetchTasks } = useU3129FindTasksForProjectHandlers();
  const [rowTasks, setRowTasks] = useState<U3129TaskItem[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const baseField = `honoraria.${index}`;

  const data = formStateHandlers.getData();
  const row = data?.honoraria?.[index];

  // Garder le total calculé en sync dans le form state pour U2000InputDecimal
  useEffect(() => {
    const currentData = formStateHandlers.getData();
    const currentRow = currentData?.honoraria?.[index];
    const total = rowTotalHours(currentRow);
    const currentTotal = (currentRow as unknown as Record<string, unknown>)?.totalHours;
    if (currentTotal !== total) {
      const list = currentData?.honoraria ?? [];
      const updated = list.map((r, i) => (i === index ? { ...r, totalHours: total } : r));
      formStateHandlers.setData({ ...currentData, honoraria: updated } as U3129Timesheet);
    }
  }, [index, formStateHandlers, row?.monday, row?.tuesday, row?.wednesday, row?.thursday, row?.friday, row?.saturday, row?.sunday]);

  const projectIdForTasks = useMemo(() => {
    const pid = row?.projectId?.trim();
    if (pid) return pid;
    return row?.project?.id?.trim() ?? "";
  }, [row?.projectId, row?.project?.id]);
  const hasProjectSelection = !!projectIdForTasks;

  const subprojectIdForTasks = useMemo(() => {
    const sid = row?.subprojectId?.trim();
    if (sid) return sid;
    const sp = (row as unknown as Record<string, unknown>)?.subproject as U3129FindSubprojectItem | undefined;
    return sp?.id?.trim() ?? "";
  }, [row]);

  const shouldUseSubprojectForTasks = showSubProjectColumn && (rowIncludeSubProject || !!subprojectIdForTasks);
  const canLoadTasks = !readonly && !!projectIdForTasks && (!shouldUseSubprojectForTasks || !!subprojectIdForTasks);

  useEffect(() => {
    if (readonly) {
      setRowTasks([]);
      return;
    }
    if (!canLoadTasks) {
      setRowTasks([]);
      if (rowClientId) {
        setHonorariumTasksForRow(rowClientId, []);
      }
      return;
    }
    let cancelled = false;
    setTasksLoading(true);
    void fetchTasks({
      projectId: projectIdForTasks,
      subprojectId: shouldUseSubprojectForTasks ? subprojectIdForTasks || null : null,
    })
      .then((list) => {
        if (cancelled) return;
        setRowTasks(list);
        if (rowClientId) {
          setHonorariumTasksForRow(rowClientId, list);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTasksLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [readonly, canLoadTasks, projectIdForTasks, subprojectIdForTasks, shouldUseSubprojectForTasks, fetchTasks, rowClientId, setHonorariumTasksForRow]);

  useEffect(() => {
    if (!row?.taskType?.trim() || !rowTasks.length) {
      return;
    }
    const match = rowTasks.find((tk) => tk.id === row.taskType?.trim());
    if (!match) {
      return;
    }
    const fields = u3129HonorariumTaskWorkNatureFieldsFromTaskItem(match);
    if (Object.keys(fields).length === 0) {
      return;
    }
    const taskRef = row.task ?? { id: row.taskType! };
    const unchanged =
      (fields.workNatureDisplay ?? "") === (taskRef.workNatureDisplay?.trim() ?? "") &&
      (fields.workNatureDescriptionFr ?? "") === (taskRef.workNatureDescriptionFr?.trim() ?? "") &&
      (fields.workNatureDescriptionEn ?? "") === (taskRef.workNatureDescriptionEn?.trim() ?? "");
    if (unchanged) {
      return;
    }
    const currentData = formStateHandlers.getData();
    const list = currentData?.honoraria ?? [];
    const updated = list.map((r, i) =>
      i === index
        ? {
            ...r,
            task: { ...taskRef, ...fields },
          }
        : r,
    );
    formStateHandlers.setData({ ...currentData, honoraria: updated } as U3129Timesheet);
  }, [row?.taskType, row?.task, rowTasks, index, formStateHandlers]);

  const taskNatureLabel = u3129TaskWorkNatureDescriptionForLanguage(row, i18n.language);

  const taskReadOnlyLabel = useMemo(() => {
    const tid = row?.taskType?.trim();
    if (!tid) return tasksLoading && canLoadTasks ? t("msgHonorariumTasksLoading", { ns: NS, defaultValue: "Chargement des tâches…" }) : "—";
    const fromList = rowTasks.find((tk) => tk.id === tid);
    if (fromList) return formatU3129TaskLabel(fromList, i18n.language);
    return row?.task?.description?.trim() || tid;
  }, [row?.taskType, row?.task?.description, rowTasks, tasksLoading, canLoadTasks, t, i18n.language]);

  // En édition, les cellules non modifiables restent grises pour se distinguer des champs de saisie (blancs).
  const childBoxOverrideClass = readonly ? U3129_HONORARIUM_EXPANDED_CHILD_BOX_CLASS : "";
  const taskRowReadonlyBoxClass = `${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} ${childBoxOverrideClass} h-full min-h-8 w-full whitespace-normal leading-tight`;
  const childReadonlyBoxClass = `${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} ${childBoxOverrideClass}`;

  const taskNatureReadonly = (
    <div className={`${taskRowReadonlyBoxClass} break-all`}>{taskNatureLabel}</div>
  );

  const taskDescriptionReadonly = (label: string) => (
    <div className={`${taskRowReadonlyBoxClass} break-words`}>{label}</div>
  );

  const taskRowGutterCell = (
    <td className={`${U3129_GRID_COMPACT_ROW_CHECKBOX_TD_CLASS} ${U3129_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS}`}>
      <div className="flex items-center justify-center gap-0.5">
        <U3129HonorariumCoherenceIcon message={taskCoherenceMessage} />
        {!readonly ? (
          <U2000CheckBox
            formStateHandlers={formStateHandlers}
            fieldName={`${baseField}.checked`}
            label=""
            readonly={checkboxDisabled}
          />
        ) : null}
      </div>
    </td>
  );

  const taskOnlyCells = (taskCell: React.ReactNode, hourCells: React.ReactNode, totalCell: React.ReactNode) => (
    <>
      {taskRowGutterCell}
      <td
        colSpan={U3129_HONORARIUM_TASK_ROW_META_COLSPAN}
        className={`${U3129_HONORARIUM_TASK_DETAIL_TD_BASE} align-top text-left ${U3129_HONORARIUM_TASK_DETAIL_COLSPAN_CLASS}`}
      >
        <div className={U3129_HONORARIUM_TASK_ROW_SPLIT_WRAPPER_CLASS}>
          <div className={U3129_HONORARIUM_TASK_ROW_TASK_PART_CLASS}>{taskCell}</div>
          <div className={U3129_HONORARIUM_TASK_ROW_NATURE_PART_CLASS}>{taskNatureReadonly}</div>
        </div>
      </td>
      {showSubProjectColumn ? (
        <td className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top`} />
      ) : null}
      <td className={U3129_TASK_ORPHAN_SLOT_TD_CLASS} aria-hidden="true" />
      {hourCells}
      {totalCell}
    </>
  );

  if (readonly && taskOnlyLayout) {
    return (
      <tr className={U3129_HONORARIUM_EXPANDED_CHILD_ROW_CLASS}>
        {taskOnlyCells(
          taskDescriptionReadonly(row?.task?.description?.trim() || row?.taskType || "—"),
          visibleDays.map((d) => (
            <td key={d} className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${U3129_HOUR_COL_CLASS} align-top text-right`}>
              <div className={`${childReadonlyBoxClass} justify-end tabular-nums`}>{formatHonorariumDayCellHours(row?.[d])}</div>
            </td>
          )),
          <td className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${U3129_HOUR_COL_CLASS} align-top text-right`}>
            <div className={`${childReadonlyBoxClass} justify-end tabular-nums`}>{formatHorizontalRowTotalHours(rowTotalHours(row))}</div>
          </td>,
        )}
      </tr>
    );
  }

  if (readonly) {
    return (
      <tr className={U3129_HONORARIUM_EXPANDED_CHILD_ROW_CLASS}>
        {taskRowGutterCell}
        <U3129LineProjectSubprojectRowCells
          baseField={baseField}
          arrayField="honoraria"
          index={index}
          formStateHandlers={formStateHandlers}
          findProjectHandlers={findProjectHandlers}
          getProjectId={getProjectId}
          readonly={readonly}
          rowIncludeSubProject={rowIncludeSubProject}
          showSubProjectColumn={showSubProjectColumn}
          clearTaskFieldsOnProjectChange
          lockProjectAndSubproject={!!row?.taskType?.trim()}
          compactTableControls
          projectReadOnlyLikeClient
          honorariumMetaColClasses={U3129_HONORARIUM_META_COL_CLASSES}
          ns={NS}
        />
        <td className={`${U3129_GRID_COMPACT_ROW_TASK_TD_BASE} align-top text-left min-w-0 ${U3129_TASK_COL_CLASS}`}>
          <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} ${U3129_HONORARIUM_TEXT_TRUNCATE_CLASS}`}>
            {row?.task?.description?.trim() || row?.taskType || "—"}
          </div>
        </td>
        {visibleDays.map((d) => (
          <td key={d} className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${U3129_HOUR_COL_CLASS} align-top text-right`}>
            <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums`}>{formatHonorariumDayCellHours(row?.[d])}</div>
          </td>
        ))}
        <td className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${U3129_HOUR_COL_CLASS} align-top text-right`}>
          <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums`}>{formatHorizontalRowTotalHours(rowTotalHours(row))}</div>
        </td>
      </tr>
    );
  }

  if (taskOnlyLayout && hasProjectSelection) {
    return (
      <tr className={`${U3129_HONORARIUM_EXPANDED_CHILD_ROW_CLASS} transition-colors`}>
        {taskOnlyCells(taskDescriptionReadonly(taskReadOnlyLabel),
          visibleDays.map((d) => (
            <td key={d} className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} align-top ${U3129_HOUR_COL_CLASS}`}>
              <div className={`${U3129_GRID_COMPACT_HOUR_CELL_INNER_CLASS} ${U3129_GRID_COMPACT_DECIMAL_CONTROL_CLASS}`}>
                <div className="relative w-full min-w-0 [&>div]:space-y-0">
                  <U2000InputDecimal
                    formStateHandlers={formStateHandlers}
                    fieldName={`${baseField}.${d}`}
                    label=""
                    readonly={false}
                    decimalDigits={2}
                    textAlign="right"
                  />
                </div>
              </div>
            </td>
          )),
          <td className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${U3129_HOUR_COL_CLASS} align-top text-right`}>
            <div className={`${childReadonlyBoxClass} justify-end tabular-nums`}>{formatHorizontalRowTotalHours(rowTotalHours(row))}</div>
          </td>,
        )}
      </tr>
    );
  }

  return (
    <tr className={`${U3129_HONORARIUM_EXPANDED_CHILD_ROW_CLASS} transition-colors`}>
      {taskRowGutterCell}
      <U3129LineProjectSubprojectRowCells
        baseField={baseField}
        arrayField="honoraria"
        index={index}
        formStateHandlers={formStateHandlers}
        findProjectHandlers={findProjectHandlers}
        getProjectId={getProjectId}
        readonly={readonly}
        rowIncludeSubProject={rowIncludeSubProject}
        showSubProjectColumn={showSubProjectColumn}
        clearTaskFieldsOnProjectChange
        lockProjectAndSubproject={!!row?.taskType?.trim()}
        compactTableControls
        projectReadOnlyLikeClient
        honorariumMetaColClasses={U3129_HONORARIUM_META_COL_CLASSES}
        ns={NS}
      />
      {!hasProjectSelection ? (
        <>
          <td className={`${U3129_GRID_COMPACT_ROW_TASK_TD_BASE} align-top text-left min-w-0 ${U3129_TASK_COL_CLASS}`}>
            <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} />
          </td>
          {visibleDays.map((d) => (
            <td key={d} className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} align-top ${U3129_HOUR_COL_CLASS} text-right`}>
              <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums`} />
            </td>
          ))}
          <td className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${U3129_HOUR_COL_CLASS} align-top text-right`}>
            <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums`} />
          </td>
        </>
      ) : (
        <>
          <td className={`${U3129_GRID_COMPACT_ROW_TASK_TD_BASE} align-top text-left min-w-0 ${U3129_TASK_COL_CLASS}`}>
            {taskDescriptionReadonly(taskReadOnlyLabel)}
          </td>
          {visibleDays.map((d) => (
            <td key={d} className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} align-top ${U3129_HOUR_COL_CLASS}`}>
              <div className={`${U3129_GRID_COMPACT_HOUR_CELL_INNER_CLASS} ${U3129_GRID_COMPACT_DECIMAL_CONTROL_CLASS}`}>
                <div className="relative w-full min-w-0 [&>div]:space-y-0">
                  <U2000InputDecimal
                    formStateHandlers={formStateHandlers}
                    fieldName={`${baseField}.${d}`}
                    label=""
                    readonly={false}
                    decimalDigits={2}
                    textAlign="right"
                  />
                </div>
              </div>
            </td>
          ))}
          <td className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${U3129_HOUR_COL_CLASS} align-top text-right`}>
            <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums`}>{formatHorizontalRowTotalHours(rowTotalHours(row))}</div>
          </td>
        </>
      )}
    </tr>
  );
}

const U3129HonorariumTab: React.FC<U3129HonorariumTabProps> = ({ formStateHandlers, coherenceWarnings }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { auth, hasRole } = useU2000AuthContext();
  const { initLU, setHonorariumTasksForRow, honorariumTasksByClientRowId } = useU3129Context();
  const showSubProjectColumn = useMemo(() => initLU.personalization?.admIncludeSubProject === "true", [initLU.personalization?.admIncludeSubProject]);
  /** Colspan pied : projet + client + natures (+ sous-projet). */
  const honorariumGroupedMetaColSpan = showSubProjectColumn ? 4 : 3;
  const [projectSortField, setProjectSortField] = useState<U3129HonorariumProjectSortField>("default");
  const [projectSortOrder, setProjectSortOrder] = useState<U3129HonorariumSortOrder>("ASC");
  const [taskSortOrder, setTaskSortOrder] = useState<U3129HonorariumSortOrder>("ASC");
  const [taskSortActive, setTaskSortActive] = useState(false);
  const [deletePendingIndices, setDeletePendingIndices] = useState<number[] | null>(null);
  const [deletePendingKind, setDeletePendingKind] = useState<U3129HonorariumDeleteConfirmKind>("lines");
  const [deletePendingPreview, setDeletePendingPreview] = useState<U3129HonorariumDeleteConfirmPreview | null>(null);
  const [expandedProjectKey, setExpandedProjectKey] = useState<string | null>(null);
  const pendingHonorariumHourFocusRef = useRef<U3129HonorariumHourCellCoord | null>(null);
  const honorariumSheetKeyForAccordionRef = useRef<string | undefined>(undefined);
  const [addHonorariumDialogOpen, setAddHonorariumDialogOpen] = useState(false);
  const [dayDialogDate, setDayDialogDate] = useState<string | null>(null);
  const honorariumTemplateStorageKey = useMemo(
    () => (auth.isAuthenticated && auth.user.userName ? getHonorariumTemplateStorageKey(auth.user.userName, auth.user.organizationId || "noorg") : "U3129_guest_honorariumTemplate"),
    [auth.isAuthenticated, auth.user.organizationId, auth.user.userName],
  );

  const [storedTemplateLineCount, setStoredTemplateLineCount] = useState(0);

  useEffect(() => {
    const refreshCount = () => {
      const rows = parseHonorariumTemplatePayload(localStorage.getItem(honorariumTemplateStorageKey));
      setStoredTemplateLineCount(rows?.length ?? 0);
    };
    refreshCount();
    const onStorage = (event: StorageEvent) => {
      if (event.key === honorariumTemplateStorageKey) refreshCount();
    };
    const onCustom = (event: Event) => {
      const detail = (event as CustomEvent<{ storageKey?: string }>).detail;
      if (!detail?.storageKey || detail.storageKey === honorariumTemplateStorageKey) refreshCount();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(U3129_HONORARIUM_TEMPLATE_CHANGED_EVENT, onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(U3129_HONORARIUM_TEMPLATE_CHANGED_EVENT, onCustom);
    };
  }, [honorariumTemplateStorageKey]);

  const findProjectHandlers = useU3129FindProjectHandlers();
  const { fetchTasksResult } = useU3129FindTasksForProjectHandlers();

  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ || (data != null && data.status !== "Open");

  const projectDayHourFooterTdClass = useMemo(
    () =>
      `${readonly ? U3129_GRID_COMPACT_HOUR_FOOTER_VALUE_TD_CLASS_READ : U3129_GRID_COMPACT_HOUR_FOOTER_VALUE_TD_CLASS_EDIT} ${U3129_HOUR_COL_CLASS}`,
    [readonly],
  );
  const projectCrossFooterCellClass = useMemo(() => `${projectDayHourFooterTdClass} text-gray-600`, [projectDayHourFooterTdClass]);

  const canViewU3147Day = hasRole(U3147ROLES.READ);
  const u3147EmployeeId = hasRole(U3129ROLES.OTHER_EMPLOYEE) ? data?.employeeId : undefined;
  const u3147WeekEnabled =
    !!data?.periodDate?.trim() && canViewU3147Day && (u3147EmployeeId == null || !!u3147EmployeeId?.trim());
  const { showWeekend, setShowWeekend, visibleDays, dayLabelsWithDates, dayDates } = useU3129TimesheetWeekView();
  const { byDate } = useU3147WeekDaySessions(data?.periodDate, u3147EmployeeId, u3147WeekEnabled);
  const projectTableEmptyColSpan = (showSubProjectColumn ? 6 : 5) + visibleDays.length + 1;

  const honorariumTableMinWidth = useMemo(
    () =>
      u3129HonorariumTableMinWidth({
        showSubProjectColumn,
        dayColumnCount: visibleDays.length,
      }),
    [showSubProjectColumn, visibleDays.length],
  );

  const honorariumTableColGroup = useMemo(
    () => (
      <colgroup>
        <col style={{ width: U3129_HONORARIUM_COL_WIDTH_GUTTER, minWidth: U3129_HONORARIUM_COL_WIDTH_GUTTER }} />
        <col
          style={{
            width: U3129_HONORARIUM_COL_WIDTH_PROJECT,
            minWidth: U3129_HONORARIUM_COL_WIDTH_PROJECT,
            maxWidth: U3129_HONORARIUM_COL_WIDTH_PROJECT,
          }}
        />
        <col style={{ minWidth: U3129_HONORARIUM_COL_WIDTH_CLIENT }} />
        <col
          style={{
            width: U3129_HONORARIUM_COL_WIDTH_NATURES,
            minWidth: U3129_HONORARIUM_COL_WIDTH_NATURES,
          }}
        />
        {showSubProjectColumn ? (
          <col
            style={{
              width: U3129_HONORARIUM_COL_WIDTH_SUBPROJECT,
              minWidth: U3129_HONORARIUM_COL_WIDTH_SUBPROJECT,
              maxWidth: U3129_HONORARIUM_COL_WIDTH_SUBPROJECT,
            }}
          />
        ) : null}
        <col
          style={{
            width: U3129_HONORARIUM_COL_WIDTH_TASK_ORPHAN,
            minWidth: U3129_HONORARIUM_COL_WIDTH_TASK_ORPHAN,
          }}
        />
        {visibleDays.map((d) => (
          <col key={d} style={{ width: U3129_HONORARIUM_COL_WIDTH_HOUR, minWidth: U3129_HONORARIUM_COL_WIDTH_HOUR }} />
        ))}
        <col style={{ width: U3129_HONORARIUM_COL_WIDTH_HOUR, minWidth: U3129_HONORARIUM_COL_WIDTH_HOUR }} />
      </colgroup>
    ),
    [showSubProjectColumn, visibleDays],
  );

  const handleHonorariumProjectSort = useCallback((field: U3129HonorariumProjectSortField) => {
    setTaskSortActive(false);
    setProjectSortField((prevField) => {
      if (prevField === field) {
        setProjectSortOrder((prevOrder) => (prevOrder === "ASC" ? "DESC" : "ASC"));
        return field;
      }
      setProjectSortOrder("ASC");
      return field;
    });
  }, []);

  const handleHonorariumTaskSort = useCallback(() => {
    setTaskSortActive(true);
    setTaskSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
  }, []);

  useEffect(() => {
    const current = formStateHandlers.getData();
    const list = current?.honoraria ?? [];
    if (!list.length) return;
    let changed = false;
    const next = list.map((r) => {
      if (r.clientRowId?.trim()) return r;
      changed = true;
      return { ...r, clientRowId: newHonorariumClientRowId() };
    });
    if (changed) {
      formStateHandlers.setData({ ...current, honoraria: next } as U3129Timesheet);
    }
  }, [data?.honoraria, formStateHandlers]);

  const rows = useMemo(() => data?.honoraria ?? [], [data?.honoraria]);

  const projectGroups = useMemo(() => {
    const grouped = u3129GroupHonorariumForDisplay(rows, { showSubProjectColumn });
    const sorted = u3129SortHonorariumProjectGroups(grouped, projectSortField, projectSortOrder);
    if (!taskSortActive || expandedProjectKey == null) {
      return sorted;
    }
    return sorted.map((g) =>
      g.projectKey === expandedProjectKey ? u3129ApplyTaskSortToProjectGroup(g, taskSortOrder) : g,
    );
  }, [rows, showSubProjectColumn, projectSortField, projectSortOrder, taskSortActive, taskSortOrder, expandedProjectKey]);

  useEffect(() => {
    const sheetKey = data?.id?.trim() || "draft";
    if (honorariumSheetKeyForAccordionRef.current !== sheetKey) {
      honorariumSheetKeyForAccordionRef.current = sheetKey;
      setExpandedProjectKey(null);
      setProjectSortField("default");
      setProjectSortOrder("ASC");
      setTaskSortActive(false);
      setTaskSortOrder("ASC");
    }
  }, [data?.id]);

  const honorariumProjectGroupKeys = useMemo(
    () => projectGroups.map((g) => g.projectKey).join('\x1e'),
    [projectGroups],
  );

  const honorariumProjectKeySet = useMemo(
    () => new Set(projectGroups.map((g) => g.projectKey)),
    [honorariumProjectGroupKeys],
  );

  useEffect(() => {
    if (honorariumProjectKeySet.size === 0) {
      setExpandedProjectKey(null);
      return;
    }
    setExpandedProjectKey((prev) => {
      if (prev == null || honorariumProjectKeySet.has(prev)) {
        return prev;
      }
      return null;
    });
  }, [honorariumProjectGroupKeys, honorariumProjectKeySet]);

  const openProjectGroup = useCallback((row: U3129HonorariumItem) => {
    setExpandedProjectKey(u3129ResolveHonorariumProjectKey(row));
  }, []);

  /** Préfixe les clés React pour forcer le remontage des champs décimaux au changement d’enregistrement (évite le flash de l’ancienne feuille). */
  const honorariumRowKeyPrefix = data?.id?.trim() || "draft";

  const hasCheckedRow = useMemo(() => rows.some((row) => (row as unknown as Record<string, unknown>).checked === true), [rows]);

  const honorariumHeaderCheckState = useMemo(
    () => u3129HonorariumHeaderCheckState(projectGroups, rows),
    [projectGroups, rows],
  );

  const setHonorariumRowsChecked = useCallback(
    (indices: readonly number[], checked: boolean) => {
      if (indices.length === 0) {
        return;
      }
      const current = formStateHandlers.getData();
      const list = [...(current?.honoraria ?? [])];
      let changed = false;
      for (const index of indices) {
        const row = list[index];
        if (!row) {
          continue;
        }
        if ((row as unknown as Record<string, unknown>).checked === checked) {
          continue;
        }
        list[index] = { ...row, checked } as U3129HonorariumItem;
        changed = true;
      }
      if (!changed) {
        return;
      }
      formStateHandlers.setData({ ...current, honoraria: list } as U3129Timesheet);
    },
    [formStateHandlers],
  );

  const handleToggleHonorariumProjectCheck = useCallback(
    (lineIndices: readonly number[]) => {
      const state = u3129HonorariumGroupCheckState(lineIndices, rows);
      const shouldCheck = state !== "all";
      setHonorariumRowsChecked(lineIndices, shouldCheck);
    },
    [rows, setHonorariumRowsChecked],
  );

  const handleToggleHonorariumSelectAll = useCallback(() => {
    const indices = u3129HonorariumIndicesForSelectAllToggle(projectGroups, rows);
    if (indices.length === 0) {
      return;
    }
    const shouldCheck = honorariumHeaderCheckState !== "all";
    setHonorariumRowsChecked(indices, shouldCheck);
  }, [honorariumHeaderCheckState, projectGroups, rows, setHonorariumRowsChecked]);

  const closeDeleteDialog = useCallback(() => {
    setDeletePendingIndices(null);
    setDeletePendingPreview(null);
    setDeletePendingKind("lines");
  }, []);

  const handleDeleteCheckedRow = useCallback(() => {
    const list = formStateHandlers.getData()?.honoraria ?? [];
    const preview = u3129HonorariumDeleteConfirmPreview(projectGroups, list);
    if (preview == null || preview.checkedIndices.length === 0) {
      return;
    }
    setDeletePendingKind(preview.kind);
    setDeletePendingPreview(preview);
    setDeletePendingIndices(preview.checkedIndices);
  }, [formStateHandlers, projectGroups]);

  const handleRequestDeleteSubproject = useCallback((indices: number[]) => {
    if (indices.length === 0) return;
    setDeletePendingKind("subproject");
    setDeletePendingPreview(null);
    setDeletePendingIndices(indices);
  }, []);

  const toggleProjectExpanded = useCallback((projectKey: string) => {
    setExpandedProjectKey((prev) => (prev === projectKey ? null : projectKey));
  }, []);

  useLayoutEffect(() => {
    const pending = pendingHonorariumHourFocusRef.current;
    if (pending == null || expandedProjectKey !== pending.projectKey) {
      return;
    }
    pendingHonorariumHourFocusRef.current = null;
    u3129FocusHonorariumHourCell(pending);
  }, [expandedProjectKey, projectGroups]);

  const handleHonorariumGridKeyDownCapture = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (readonly) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }
      if (!U3129_HONORARIUM_HOUR_GRID_ARROW_KEYS.has(e.key)) {
        return;
      }
      const active = document.activeElement;
      if (!(active instanceof HTMLInputElement) || !active.id) {
        return;
      }
      const parsed = u3129ParseHonorariumHourFieldId(active.id);
      if (parsed == null || expandedProjectKey == null) {
        return;
      }
      e.preventDefault();
      const next = u3129ResolveHonorariumHourCellAfterArrow(
        e.key,
        projectGroups,
        { projectKey: expandedProjectKey, ...parsed },
        visibleDays,
      );
      if (next == null) {
        return;
      }
      if (next.projectKey !== expandedProjectKey) {
        pendingHonorariumHourFocusRef.current = next;
        setExpandedProjectKey(next.projectKey);
      } else {
        u3129FocusHonorariumHourCell(next);
      }
    },
    [readonly, expandedProjectKey, projectGroups, visibleDays],
  );

  const footerTotals = useMemo(() => u2000SumHonorariumDayTotals(rows), [rows]);
  const bankHoursList = useMemo(() => ensureBankHoursList(data?.bankHoursList), [data?.bankHoursList]);
  const absenceCrossFooterTotals = useMemo(() => u2000SumCrossTabAbsenceHoursFooter(bankHoursList), [bankHoursList]);
  const specialNetCrossFooterTotals = useMemo(() => u2000SumCrossTabSpecialNetHoursFooter(bankHoursList), [bankHoursList]);
  const workedCrossFooterTotals = useMemo(() => {
    const daySums: Record<string, number> = {};
    for (const d of DAYS) {
      daySums[d] =
        (footerTotals.daySums[d] ?? 0) + (absenceCrossFooterTotals.daySums[d] ?? 0) + (specialNetCrossFooterTotals.daySums[d] ?? 0);
    }
    return {
      daySums,
      grandTotal: footerTotals.grandTotal + absenceCrossFooterTotals.grandTotal + specialNetCrossFooterTotals.grandTotal,
    };
  }, [absenceCrossFooterTotals, footerTotals, specialNetCrossFooterTotals]);

  useEffect(() => {
    const current = formStateHandlers.getData();
    if (current == null) return;
    const stored = current.honorariaFooterTotals;
    const needUpdate = !stored || DAYS.some((d) => (stored[d] ?? 0) !== footerTotals.daySums[d]) || (stored.grandTotal ?? 0) !== footerTotals.grandTotal;
    if (needUpdate) {
      formStateHandlers.setData({
        ...current,
        honorariaFooterTotals: {
          ...footerTotals.daySums,
          grandTotal: footerTotals.grandTotal,
        },
      });
    }
  }, [footerTotals, formStateHandlers]);

  const handleOpenAddHonorariumDialog = useCallback(() => {
    setAddHonorariumDialogOpen(true);
  }, []);

  const handleConfirmAddHonorariumLines = useCallback(
    (newRows: U3129HonorariumItem[], tasksForCacheByProjectId: Record<string, U3129TaskItem[]>) => {
      const current = formStateHandlers.getData();
      if (!current) return;
      const list = current.honoraria ?? [];
      const toAdd = u3129FilterHonorariumLinesNotDuplicatingExisting(newRows, list);
      const skipped = newRows.length - toAdd.length;
      if (toAdd.length === 0) {
        toast.warning(
          t("msgAddHonorariumLinesAllDuplicates", {
            ns: NS,
            defaultValue: "Aucune ligne ajoutée : les tâches sélectionnées sont déjà présentes pour ce projet.",
          }),
        );
        return;
      }
      if (skipped > 0) {
        toast.info(
          t("msgAddHonorariumLinesPartial", {
            ns: NS,
            defaultValue: "{{added}} ligne(s) ajoutée(s). {{skipped}} ignorée(s) (déjà présentes).",
            added: toAdd.length,
            skipped,
          }),
        );
      }
      const nextHonoraria = [...list, ...toAdd];
      const nextData = { ...current, honoraria: nextHonoraria } as U3129Timesheet;
      formStateHandlers.setData(nextData);
      toAdd.forEach((r) => {
        const cid = r.clientRowId?.trim();
        const pid = r.projectId?.trim() ?? (typeof r.project === 'object' && r.project && 'id' in r.project ? String(r.project.id ?? '').trim() : '');
        const tasksForRow = pid ? tasksForCacheByProjectId[pid] ?? [] : [];
        if (cid) setHonorariumTasksForRow(cid, tasksForRow);
      });
      const validateForm = formStateHandlers.getBusinessHandlers()?.validateForm;
      if (validateForm) {
        formStateHandlers.setErrors(validateForm(nextData));
      }
    },
    [formStateHandlers, setHonorariumTasksForRow, t, toast],
  );

  const handleSaveHonorariumTemplate = useCallback(() => {
    const list = formStateHandlers.getData()?.honoraria ?? [];
    const templateRows = serializeHonorariumTemplate(list, {
      resolveTask: (row) => u3129FindTaskInHonorariumCache(row, honorariumTasksByClientRowId),
    });
    if (templateRows.length === 0) {
      toast.error(t("msgHonorariumTemplateEmpty", { ns: NS, defaultValue: "Aucune ligne avec projet à enregistrer comme modèle." }));
      return;
    }
    if (writeHonorariumTemplateToStorage(honorariumTemplateStorageKey, templateRows)) {
      setStoredTemplateLineCount(templateRows.length);
      toast.success(t("msgHonorariumTemplateSaved", { ns: NS, defaultValue: "Modèle d’honoraires enregistré sur cet appareil ({{count}} ligne(s)).", count: templateRows.length }));
    } else {
      toast.error(t("msgHonorariumTemplateSaveFailed", { ns: NS, defaultValue: "Impossible d’enregistrer le modèle (stockage local)." }));
    }
  }, [formStateHandlers, honorariumTemplateStorageKey, honorariumTasksByClientRowId, t, toast]);

  const handleApplyHonorariumTemplate = useCallback(async () => {
    const rows = parseHonorariumTemplatePayload(localStorage.getItem(honorariumTemplateStorageKey));
    if (rows == null || rows.length === 0) {
      toast.error(t("msgHonorariumTemplateNone", { ns: NS, defaultValue: "Aucun modèle enregistré." }));
      return;
    }
    const current = formStateHandlers.getData();
    if (!current) return;
    const existing = current.honoraria ?? [];
    const templateEmployeeId = hasRole(U3129ROLES.OTHER_EMPLOYEE) ? current.employeeId ?? undefined : undefined;

    const scopes = u3129UniqueFindTasksBodiesFromTemplateRows(rows);
    const resolutions = new Map<string, U3129HonorariumTemplateScopeResolution>();
    const tasksByScopeKey = new Map<string, U3129TaskItem[]>();

    await Promise.all(
      scopes.map(async ({ scopeKey, body }) => {
        const requestBody = {
          projectId: body.projectId,
          subprojectId: body.subprojectId,
          employeeId: templateEmployeeId,
        };
        // Toujours relire l’API : le cache partagé peut encore contenir des tâches retirées du projet (U3121).
        u3129InvalidateFindTasksForProjectCache(u3129BuildFindTasksForProjectCacheKey(requestBody));
        const result = await fetchTasksResult(requestBody, { silent: true });
        if (!result.ok) {
          resolutions.set(scopeKey, { status: "unusable" });
          tasksByScopeKey.set(scopeKey, []);
          return;
        }
        const taskIds = new Set(result.tasks.map((task) => task.id.trim()).filter(Boolean));
        resolutions.set(scopeKey, { status: "usable", taskIds });
        tasksByScopeKey.set(scopeKey, result.tasks);
      }),
    );

    const { kept, ignoredInactive } = u3129PartitionHonorariumTemplateRowsByActiveAssignments(rows, resolutions);
    const candidates = templateRowsToHonorariumItems(kept);
    const toAdd = u3129FilterHonorariumLinesNotDuplicatingExisting(candidates, existing);
    const skippedDup = candidates.length - toAdd.length;
    const skippedInactive = ignoredInactive.length;

    if (toAdd.length === 0) {
      toast.warning(
        t("msgHonorariumTemplateApplyNone", {
          ns: NS,
          defaultValue:
            "Aucune ligne ajoutée : le modèle ne contient aucune ligne applicable (déjà présentes, projet ou tâche inactif).",
          skippedDup,
          skippedInactive,
        }),
      );
      return;
    }

    const nextData = { ...current, honoraria: [...existing, ...toAdd] } as U3129Timesheet;
    formStateHandlers.setData(nextData);
    toAdd.forEach((row) => {
      const clientRowId = row.clientRowId?.trim();
      const projectId = row.projectId?.trim() ?? "";
      const subprojectId = row.subprojectId?.trim() ?? "";
      const scopeKey = u3129HonorariumTemplateScopeKey({ projectId, subprojectId });
      const tasksForRow = tasksByScopeKey.get(scopeKey) ?? [];
      if (clientRowId) {
        setHonorariumTasksForRow(clientRowId, tasksForRow);
      }
    });
    const validateForm = formStateHandlers.getBusinessHandlers()?.validateForm;
    if (validateForm) {
      formStateHandlers.setErrors(validateForm(nextData));
    }
    if (skippedDup > 0 || skippedInactive > 0) {
      toast.info(
        t("msgHonorariumTemplateApplyMixed", {
          ns: NS,
          defaultValue:
            "{{added}} ligne(s) du modèle ajoutée(s). {{skippedDup}} déjà présente(s). {{skippedInactive}} ignorée(s) (projet ou tâche inactif).",
          added: toAdd.length,
          skippedDup,
          skippedInactive,
        }),
      );
    } else {
      toast.success(
        t("msgHonorariumTemplateApplied", { ns: NS, defaultValue: "{{count}} ligne(s) du modèle ajoutée(s).", count: toAdd.length }),
      );
    }
    openProjectGroup(toAdd[0]);
  }, [
    fetchTasksResult,
    formStateHandlers,
    hasRole,
    honorariumTemplateStorageKey,
    openProjectGroup,
    setHonorariumTasksForRow,
    t,
    toast,
  ]);

  const confirmDeleteRow = useCallback(() => {
    if (deletePendingIndices == null || deletePendingIndices.length === 0) return;
    const toRemove = new Set(deletePendingIndices);
    const current = formStateHandlers.getData();
    const list = current?.honoraria ?? [];
    const next = list.filter((_, i) => !toRemove.has(i));
    const nextData = {
      ...current,
      honoraria: next,
    } as U3129Timesheet;
    formStateHandlers.setData(nextData);
    const validateForm = formStateHandlers.getBusinessHandlers()?.validateForm;
    formStateHandlers.setErrors(validateForm ? validateForm(nextData) : {});
    closeDeleteDialog();
  }, [closeDeleteDialog, deletePendingIndices, formStateHandlers]);

  const getProjectIdForRow = useCallback(
    (index: number) => {
      const r = formStateHandlers.getData()?.honoraria?.[index];
      const proj = r && (r as unknown as Record<string, unknown>).project;
      const pid = proj && typeof proj === "object" && "id" in proj ? String((proj as { id?: string }).id ?? "") : "";
      return r?.projectId?.trim() || pid || undefined;
    },
    [formStateHandlers],
  );

  const getRowIncludeSubProject = useCallback(
    (index: number): boolean => {
      const r = formStateHandlers.getData()?.honoraria?.[index];
      const project = (r as unknown as Record<string, unknown>)?.project as U3129FindProjectItem | undefined;
      const v = project?.includeSubProjet;
      return v === true || (typeof v === "string" && v === "true");
    },
    [formStateHandlers],
  );

  const deleteDialogOpen = deletePendingIndices != null && deletePendingIndices.length > 0;

  const deleteDialogTitle = useMemo(() => {
    if (!deleteDialogOpen) {
      return "";
    }
    if (deletePendingKind === "subproject") {
      return t("msgConfirmDeleteHonorariumSubproject", {
        ns: NS,
        count: deletePendingIndices!.length,
        defaultValue: "Supprimer ce sous-projet ({{count}} ligne(s)) ?",
      });
    }
    if (deletePendingKind === "projects" && deletePendingPreview != null) {
      if (deletePendingPreview.fullProjects.length === 1) {
        const p = deletePendingPreview.fullProjects[0];
        return t("msgConfirmDeleteHonorariumProject", {
          ns: NS,
          project: p.projectLabel,
          client: p.clientLabel,
          defaultValue: "Retirer le projet {{project}} — {{client}} de cette feuille ?",
        });
      }
      return t("msgConfirmDeleteHonorariumProjectsBulk", {
        ns: NS,
        count: deletePendingPreview.fullProjects.length,
        defaultValue: "Retirer {{count}} projets de cette feuille ?",
      });
    }
    if (deletePendingKind === "mixed" && deletePendingPreview != null) {
      return t("msgConfirmDeleteHonorariumMixed", {
        ns: NS,
        projectCount: deletePendingPreview.fullProjects.length,
        lineCount: deletePendingPreview.looseLineCount,
        defaultValue: "Retirer {{projectCount}} projet(s) et {{lineCount}} tâche(s) de cette feuille ?",
      });
    }
    if (deletePendingIndices!.length === 1) {
      return t("msgConfirmDeleteHonorariumRow", { ns: NS, defaultValue: "Supprimer cette ligne ?" });
    }
    return t("msgConfirmDeleteHonorariumRowsBulk", {
      ns: NS,
      count: deletePendingIndices!.length,
      defaultValue: "Supprimer {{count}} lignes d’honoraires ?",
    });
  }, [deleteDialogOpen, deletePendingIndices, deletePendingKind, deletePendingPreview, t]);

  const deleteDialogDetail = useMemo(() => {
    if (!deleteDialogOpen) {
      return null;
    }
    if (deletePendingKind === "subproject") {
      return (
        <p className="text-sm text-gray-600">
          {t("msgConfirmDeleteHonorariumSubprojectDetail", {
            ns: NS,
            count: deletePendingIndices!.length,
            defaultValue: "Toutes les tâches de ce sous-projet seront retirées de la feuille.",
          })}
        </p>
      );
    }
    if (deletePendingKind === "projects" && deletePendingPreview != null) {
      const single = deletePendingPreview.fullProjects.length === 1 ? deletePendingPreview.fullProjects[0] : null;
      return (
        <div className="space-y-2 text-sm text-gray-600">
          {single != null && single.hours > 0 ? (
            <p>
              {t("msgConfirmDeleteHonorariumProjectDetailHours", {
                ns: NS,
                count: single.lineCount,
                hours: single.hours.toFixed(2),
                defaultValue: "{{count}} tâche(s) et {{hours}} h seront retirées de cette feuille. Le dossier projet reste inchangé.",
              })}
            </p>
          ) : (
            <p>
              {t("msgConfirmDeleteHonorariumProjectDetail", {
                ns: NS,
                defaultValue: "Les tâches cochées seront retirées de cette feuille. Le dossier projet reste inchangé.",
              })}
            </p>
          )}
          {deletePendingPreview.fullProjects.length > 1 ? (
            <ul className="list-inside list-disc text-left">
              {deletePendingPreview.fullProjects.map((p) => (
                <li key={p.projectKey}>
                  {p.projectLabel} — {p.clientLabel}
                  {p.hours > 0 ? ` (${p.hours.toFixed(2)} h)` : ""}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      );
    }
    if (deletePendingKind === "mixed" && deletePendingPreview != null) {
      return (
        <div className="space-y-2 text-sm text-gray-600">
          <p>
            {t("msgConfirmDeleteHonorariumMixedDetail", {
              ns: NS,
              defaultValue: "Les projets entiers et les tâches cochées seront retirés de cette feuille. Enregistrez la feuille pour conserver le changement.",
            })}
          </p>
          {deletePendingPreview.fullProjects.length > 0 ? (
            <ul className="list-inside list-disc text-left">
              {deletePendingPreview.fullProjects.map((p) => (
                <li key={p.projectKey}>
                  {p.projectLabel} — {p.clientLabel}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      );
    }
    if (deletePendingIndices!.length === 1) {
      return (
        <p className="text-sm text-gray-600">
          {t("msgConfirmDeleteHonorariumRowDetail", {
            ns: NS,
            defaultValue: "Voulez-vous supprimer cette entrée de la liste des honoraires ?",
          })}
        </p>
      );
    }
    return (
      <p className="text-sm text-gray-600">
        {t("msgConfirmDeleteHonorariumRowsBulkDetail", {
          ns: NS,
          count: deletePendingIndices!.length,
          defaultValue: "Les {{count}} lignes cochées seront retirées de la liste des honoraires.",
        })}
      </p>
    );
  }, [deleteDialogOpen, deletePendingIndices, deletePendingKind, deletePendingPreview, t]);

  // Hauteur naturelle : le tabpanel U2000 (overflow-y-auto) défile — pas de scroll imbriqué dans la grille.
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-gray-200 bg-muted/10 px-4 pb-2 pt-3">
      <div className="relative flex min-w-0 flex-col gap-2">
          <U2000YesNoDialog
            isOpen={deleteDialogOpen}
            onClose={(result) => {
              if (result === "yes") {
                confirmDeleteRow();
              } else {
                closeDeleteDialog();
              }
            }}
            title={deleteDialogTitle}
            width="28rem"
            overlayClassName="z-100"
          >
            {deleteDialogDetail}
          </U2000YesNoDialog>

          <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-x-3 gap-y-2">
            {!readonly ? (
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <U2000ActionBtn label={t("btnAdd", { ns: NS })} icon={FaPlus} handleClick={handleOpenAddHonorariumDialog} />
                <U2000ActionBtn
                  label={t("btnDelete", { ns: NS })}
                  icon={FaTrash}
                  handleClick={handleDeleteCheckedRow}
                  disabled={!hasCheckedRow}
                  title={
                    hasCheckedRow
                      ? undefined
                      : t("titleDeleteHonorariumDisabled", {
                          ns: NS,
                          defaultValue: "Cochez un projet ou des tâches",
                        })
                  }
                  className="bg-red-600! hover:bg-red-700! focus:ring-red-500! active:bg-red-800! disabled:bg-gray-400!"
                />
                <U2000ActionBtn
                  label={t("btnSaveHonorariumTemplate", { ns: NS, defaultValue: "Enregistrer le modèle" })}
                  icon={FaBookmark}
                  handleClick={handleSaveHonorariumTemplate}
                  className="bg-emerald-600! hover:bg-emerald-700! focus:ring-emerald-500! active:bg-emerald-800!"
                />
                <U2000ActionBtn
                  label={t("btnApplyHonorariumTemplate", { ns: NS, defaultValue: "Appliquer le modèle" })}
                  icon={FaClone}
                  handleClick={handleApplyHonorariumTemplate}
                  disabled={storedTemplateLineCount === 0}
                  className="bg-white! text-slate-800! ring-1! ring-slate-300! hover:bg-slate-50! focus:ring-slate-400! disabled:bg-gray-100! disabled:text-gray-400!"
                />
              </div>
            ) : null}
            <div className={`shrink-0 ${readonly ? "ml-auto w-full flex justify-end" : ""}`}>
              <label className="flex min-h-9 shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700">
                <input type="checkbox" className="h-4 w-4 shrink-0" checked={showWeekend} onChange={(e) => setShowWeekend(e.target.checked)} />
                {t("lblShowWeekendDays", { ns: NS, defaultValue: "Afficher samedi et dimanche" })}
              </label>
            </div>
          </div>

          <div
            className={`min-w-0 overflow-x-auto rounded-lg border border-gray-200 ${U3129_GRID_COMPACT_TABLE_CONTROL_CLASS} [&_input:not([data-u2000-skip-grid-compact])]:!px-2.5`}
            onKeyDownCapture={handleHonorariumGridKeyDownCapture}
          >
                  <table
                    className="table-fixed border-collapse text-sm tabular-nums"
                    style={{ width: "100%", minWidth: honorariumTableMinWidth }}
                  >
                    {honorariumTableColGroup}
                    <thead className="border-b border-gray-200 bg-muted">
                      <tr>
                        <th className={`${U3129_HONORARIUM_GUTTER_TD_CLASS} py-2.5`}>
                          <div className="flex items-center justify-center gap-0.5">
                            {!readonly && rows.length > 0 ? (
                              <U2000CheckBoxBase
                                fieldName="u3129-honorarium-select-all"
                                label=""
                                compact
                                value={honorariumHeaderCheckState === "all"}
                                indeterminate={honorariumHeaderCheckState === "partial"}
                                ariaLabel={t("btnSelectAllHonorariumProjects", {
                                  ns: NS,
                                  defaultValue: "Sélectionner tous les projets",
                                })}
                                setValue={handleToggleHonorariumSelectAll}
                              />
                            ) : null}
                            <span className="inline-flex h-8 w-8 shrink-0" aria-hidden />
                          </div>
                        </th>
                        <U3129HonorariumSortableMetaHeader
                          ns={NS}
                          labelKey="lblProject"
                          labelDefault="Projet"
                          colClass={U3129_PROJECT_COL_CLASS}
                          sortField="project"
                          activeProjectField={projectSortField}
                          activeProjectOrder={projectSortOrder}
                          activeTaskOrder={taskSortOrder}
                          taskSortActive={taskSortActive}
                          onSortProject={handleHonorariumProjectSort}
                          onSortTask={handleHonorariumTaskSort}
                        />
                        <U3129HonorariumSortableMetaHeader
                          ns={NS}
                          labelKey="lblCustomer"
                          labelDefault="Client"
                          colClass={U3129_CUSTOMER_COL_CLASS}
                          sortField="customer"
                          activeProjectField={projectSortField}
                          activeProjectOrder={projectSortOrder}
                          activeTaskOrder={taskSortOrder}
                          taskSortActive={taskSortActive}
                          onSortProject={handleHonorariumProjectSort}
                          onSortTask={handleHonorariumTaskSort}
                        />
                        <U3129HonorariumSortableMetaHeader
                          ns={NS}
                          labelKey="lblWorkNatures"
                          labelDefault="Natures"
                          colClass={U3129_HONORARIUM_NATURES_COL_CLASS}
                          sortField="natures"
                          activeProjectField={projectSortField}
                          activeProjectOrder={projectSortOrder}
                          activeTaskOrder={taskSortOrder}
                          taskSortActive={taskSortActive}
                          onSortProject={handleHonorariumProjectSort}
                          onSortTask={handleHonorariumTaskSort}
                        />
                        {showSubProjectColumn ? (
                          <th
                            className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-2.5 text-left font-medium text-gray-700 ${U3129_SUBPROJECT_COL_CLASS}`}
                          >
                            {t("lblSubProject", { ns: NS, defaultValue: "Sous-projet" })}
                          </th>
                        ) : null}
                        <U3129HonorariumSortableMetaHeader
                          ns={NS}
                          labelKey="lblTask"
                          labelDefault="Tâche"
                          colClass={`${U3129_TASK_COL_CLASS} min-w-[4rem]! w-auto! max-w-none!`}
                          sortField="task"
                          activeProjectField={projectSortField}
                          activeProjectOrder={projectSortOrder}
                          activeTaskOrder={taskSortOrder}
                          taskSortActive={taskSortActive}
                          onSortProject={handleHonorariumProjectSort}
                          onSortTask={handleHonorariumTaskSort}
                        />
                        {visibleDays.map((d) => (
                          <th key={d} className={`${U3129_PROJECT_DAY_HOUR_TH_CLASS} font-medium text-gray-700`}>
                            <span className="inline-flex items-center justify-end gap-0.5">
                              <span>{dayLabelsWithDates?.[d] ?? t(`lbl${d.charAt(0).toUpperCase()}${d.slice(1)}`, { ns: NS })}</span>
                              {dayDates?.[d] && (byDate[dayDates[d]]?.sessions?.length ?? 0) > 0 && (
                                <button
                                  type="button"
                                  title={t("btnViewTimeTrackerDay", { ns: NS, defaultValue: "Voir le suivi de temps du jour" })}
                                  onClick={() => setDayDialogDate(dayDates[d])}
                                  className="inline-flex shrink-0 text-gray-500 hover:text-blue-600"
                                >
                                  <FaRegClock className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </span>
                          </th>
                        ))}
                        <th className={`${U3129_PROJECT_DAY_HOUR_TH_CLASS} font-medium text-gray-700`}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={projectTableEmptyColSpan} className="px-4 py-8 text-center text-sm text-gray-400">
                            {t("msgNoHonorariumProjectLines", { ns: NS, defaultValue: "Aucun projet pour cette période." })}
                          </td>
                        </tr>
                      ) : (
                        projectGroups.map((projectGroup, groupIndex) => {
                          const expanded = expandedProjectKey === projectGroup.projectKey;
                          const panelId = `u3129-honorarium-project-${projectGroup.projectKey}`;
                          const useSubprojectHeaders =
                            showSubProjectColumn &&
                            projectGroup.includeSubProject &&
                            projectGroup.projectKey !== U3129_HONORARIUM_NO_PROJECT_KEY;
                          const useTaskOnlyRows = projectGroup.projectKey !== U3129_HONORARIUM_NO_PROJECT_KEY;

                          return (
                            <React.Fragment key={projectGroup.projectKey}>
                              <U3129HonorariumProjectHeaderRow
                                group={projectGroup}
                                expanded={expanded}
                                onToggle={() => toggleProjectExpanded(projectGroup.projectKey)}
                                visibleDays={visibleDays}
                                showSubProjectColumn={showSubProjectColumn}
                                isFirstProject={groupIndex === 0}
                                projectColClass={U3129_PROJECT_COL_CLASS}
                                customerColClass={U3129_CUSTOMER_COL_CLASS}
                                naturesColClass={U3129_HONORARIUM_NATURES_COL_CLASS}
                                subprojectColClass={U3129_SUBPROJECT_COL_CLASS}
                                hourColClass={U3129_HOUR_COL_CLASS}
                                formatDayHours={(v) => formatHonorariumDayCellHours(v)}
                                formatTotalHours={formatHorizontalRowTotalHours}
                                panelId={panelId}
                                showSelectionCheckbox={!readonly}
                                groupCheckState={u3129HonorariumGroupCheckState(
                                  projectGroup.allLines.map((l) => l.index),
                                  rows,
                                )}
                                onToggleGroupCheck={() =>
                                  handleToggleHonorariumProjectCheck(projectGroup.allLines.map((l) => l.index))
                                }
                                projectCoherenceMessage={u3129HonorariumCoherenceMessageForProjectGroup(
                                  projectGroup,
                                  coherenceWarnings,
                                  { projectExpanded: expanded },
                                )}
                              />
                              {expanded
                                ? projectGroup.subprojectGroups.map((subGroup) => (
                                    <React.Fragment key={`${projectGroup.projectKey}-${subGroup.subprojectKey}`}>
                                      {useSubprojectHeaders ? (
                                        <U3129HonorariumSubprojectHeaderRow
                                          group={subGroup}
                                          visibleDays={visibleDays}
                                          showSubProjectColumn={showSubProjectColumn}
                                          metaColSpan={honorariumGroupedMetaColSpan}
                                          hourColClass={U3129_HOUR_COL_CLASS}
                                          readonly={readonly}
                                          formatDayHours={(v) => formatHonorariumDayCellHours(v)}
                                          formatTotalHours={formatHorizontalRowTotalHours}
                                          onDeleteSubproject={() =>
                                            handleRequestDeleteSubproject(subGroup.lines.map((l) => l.index))
                                          }
                                        />
                                      ) : null}
                                      {subGroup.lines.map(({ index: idx, row }) => {
                                        const pid = row.projectId?.trim() || row.project?.id?.trim() || "";
                                        const taskOnly = useTaskOnlyRows && !!pid;
                                        return (
                                          <HonorariumRowEditor
                                            key={`${honorariumRowKeyPrefix}-${row.clientRowId ?? `idx-${idx}`}`}
                                            index={idx}
                                            formStateHandlers={formStateHandlers}
                                            findProjectHandlers={findProjectHandlers}
                                            getProjectId={() => getProjectIdForRow(idx)}
                                            rowClientId={row.clientRowId?.trim() ?? ""}
                                            readonly={readonly}
                                            showSubProjectColumn={showSubProjectColumn}
                                            rowIncludeSubProject={getRowIncludeSubProject(idx)}
                                            visibleDays={visibleDays}
                                            taskOnlyLayout={taskOnly}
                                            taskCoherenceMessage={
                                              coherenceWarnings?.taskByLineKey.get(
                                                u3129HonorariumCoherenceLineKey(row, idx),
                                              ) ?? ""
                                            }
                                          />
                                        );
                                      })}
                                    </React.Fragment>
                                  ))
                                : null}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                    <tfoot className="border-t-2 border-gray-200 bg-muted shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.05)]">
                      <tr aria-label={t("lblTotalProjectHours", { ns: NS, defaultValue: "Total projet" })}>
                        <td className={U3129_GRID_COMPACT_FOOTER_GUTTER_TD_CLASS} />
                        <td className={`${U3129_GRID_COMPACT_FOOTER_LABEL_TD_BASE} font-semibold text-gray-800 ${U3129_TOTAL_LABEL_COL_CLASS}`} colSpan={honorariumGroupedMetaColSpan}>
                          {t("lblTotalProjectHours", { ns: NS, defaultValue: "Total projet" })}
                        </td>
                        <td className={U3129_TASK_ORPHAN_SLOT_TD_CLASS} aria-hidden="true" />
                        {visibleDays.map((d) => (
                          <td key={d} className={`${projectDayHourFooterTdClass} font-semibold text-gray-800`}>
                            {(footerTotals.daySums[d] ?? 0).toFixed(2)}
                          </td>
                        ))}
                        <td className={`${projectDayHourFooterTdClass} font-bold text-gray-900`}>{footerTotals.grandTotal.toFixed(2)}</td>
                      </tr>
                      <tr aria-label={t("lblCrossTotalAbsenceHours", { ns: NS, defaultValue: "Total absence" })}>
                        <td className={U3129_GRID_COMPACT_FOOTER_GUTTER_TD_CLASS} />
                        <td className={`${U3129_GRID_COMPACT_FOOTER_LABEL_TD_BASE} text-sm font-medium text-gray-600 ${U3129_TOTAL_LABEL_COL_CLASS}`} colSpan={honorariumGroupedMetaColSpan}>
                          {t("lblCrossTotalAbsenceHours", { ns: NS, defaultValue: "Total absence" })}
                        </td>
                        <td className={U3129_TASK_ORPHAN_SLOT_TD_CLASS} aria-hidden="true" />
                        {visibleDays.map((d) => (
                          <td key={d} className={projectCrossFooterCellClass}>
                            {(absenceCrossFooterTotals.daySums[d] ?? 0).toFixed(2)}
                          </td>
                        ))}
                        <td className={`${projectCrossFooterCellClass} font-semibold`}>{absenceCrossFooterTotals.grandTotal.toFixed(2)}</td>
                      </tr>
                      <tr aria-label={t("lblCrossTotalSpecialBankNetHours", { ns: NS, defaultValue: "Total heures accumulées (net)" })}>
                        <td className={U3129_GRID_COMPACT_FOOTER_GUTTER_TD_CLASS} />
                        <td className={`${U3129_GRID_COMPACT_FOOTER_LABEL_TD_BASE} text-sm font-medium text-gray-600 ${U3129_TOTAL_LABEL_COL_CLASS}`} colSpan={honorariumGroupedMetaColSpan}>
                          {t("lblCrossTotalSpecialBankNetHours", { ns: NS, defaultValue: "Total heures accumulées (net)" })}
                        </td>
                        <td className={U3129_TASK_ORPHAN_SLOT_TD_CLASS} aria-hidden="true" />
                        {visibleDays.map((d) => (
                          <td key={d} className={projectCrossFooterCellClass}>
                            {(specialNetCrossFooterTotals.daySums[d] ?? 0).toFixed(2)}
                          </td>
                        ))}
                        <td className={`${projectCrossFooterCellClass} font-semibold`}>{specialNetCrossFooterTotals.grandTotal.toFixed(2)}</td>
                      </tr>
                      <tr aria-label={t("lblHeaderTotalHonorariumHours", { ns: NS, defaultValue: "Total honoraires" })}>
                        <td className={U3129_GRID_COMPACT_FOOTER_GUTTER_TD_CLASS} />
                        <td className={`${U3129_GRID_COMPACT_FOOTER_LABEL_TD_BASE} text-sm font-semibold text-gray-800 ${U3129_TOTAL_LABEL_COL_CLASS}`} colSpan={honorariumGroupedMetaColSpan}>
                          {t("lblHeaderTotalHonorariumHours", { ns: NS, defaultValue: "Total honoraires" })}
                        </td>
                        <td className={U3129_TASK_ORPHAN_SLOT_TD_CLASS} aria-hidden="true" />
                        {visibleDays.map((d) => (
                          <td key={d} className={`${projectDayHourFooterTdClass} font-semibold text-gray-800`}>
                            {(workedCrossFooterTotals.daySums[d] ?? 0).toFixed(2)}
                          </td>
                        ))}
                        <td className={`${projectDayHourFooterTdClass} font-bold text-gray-900`}>{workedCrossFooterTotals.grandTotal.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
          </div>
      </div>
      <U3129AddHonorariumLinesDialog
        open={addHonorariumDialogOpen}
        onClose={() => setAddHonorariumDialogOpen(false)}
        existingHonoraria={rows}
        onConfirm={handleConfirmAddHonorariumLines}
        admIncludeSubProjectEnabled={showSubProjectColumn}
      />
      <U3147DayDetailReadonlyDialog
        isOpen={dayDialogDate != null}
        onClose={() => setDayDialogDate(null)}
        date={dayDialogDate ?? ""}
        dayResult={dayDialogDate ? (byDate[dayDialogDate] ?? null) : null}
        showSubproject={showSubProjectColumn}
      />
    </div>
  );
};

export default U3129HonorariumTab;
